import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { OpmSearchComponent } from './components/opm/opm-search/opm-search.component';
import { OpmGrid } from './components/opm/opm-grid/opm-grid.component';
import { ErrorHandlingInterceptorService } from './services/error-handling-interceptor.service';
import { UserRepository } from './repositories/user.repository';
import { UserService } from "./services/user.service";
import { PopupService } from "./services/popup.service";
import { GridResultService } from "./services/gridresult.service";
import { PageService } from "./services/page.service";
import { TimeoutService } from "./services/timeout.service";
import { FormsModule } from '@angular/forms';
import { OpmAuditLogGridComponent } from './components/opm/opm-audit-log-grid/opm-audit-log-grid.component';
import { OpmEditProfileComponent } from './components/opm/opm-edit-profile/opm-edit-profile.component';
import { ModalPopupComponent } from './components/opm/modal-popup/modal-popup.component';
import { CurrentUserComponent } from './components/header/current-user/current-user.component';
import { HelpMenuComponent } from './components/header/help-menu/help-menu.component';
import { UserPhotoComponent } from './components/header/user-photo/user-photo.component';
import { BooleanFormatterPipe } from './pipes/boolean-formatter.pipe';
import { MilisecondsToSecondsPipe } from './pipes/miliseconds-to-seconds.pipe';
import { MilisecondsToMinutesPipe } from './pipes/miliseconds-to-minutes.pipe';
import { OpmPagingComponent } from './components/opm/opm-paging/opm-paging.component';
import { TimeoutComponent } from './components/opm/timeout/timeout.component';
import { ErrorHandlerService } from './services/error-handler.service';
import { ApplicationInsightsService } from './services/application-insights.service';
import { OpmCreateuserProfileComponent } from './components/opm/opm-createuser-profile/opm-createuser-profile.component';
import { OpmContainermappingsComponent } from './components/opm/opm-containermappings/opm-containermappings.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { OpmBasemappingsComponent } from './components/opm/opm-basemappings/opm-basemappings.component';
import { ContainermappingService } from './services/containermapping.service';
import { ContainerMappingRepository } from './repositories/containermapping.repository';
import { OpmOnboardedappsComponent } from './components/opm/opm-onboardedapps/opm-onboardedapps.component';
import { OpmDqGateReviewComponent } from './components/opm/opm-dq-gate-review/opm-dq-gate-review.component';
import { DqGateService } from './services/dq-gate.service';
import { DqGateRepository } from './repositories/dq-gate.repository';

// MSAL imports
import {
  MsalModule,
  MsalRedirectComponent,
  MsalGuard,
  MsalInterceptor,
  MsalService,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration
} from '@azure/msal-angular';
import { PublicClientApplication, InteractionType, IPublicClientApplication } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

// Import the config service
import { MsalConfigService } from './services/msal-config.service';

const routes: Routes = [
  { path: '', component: OpmSearchComponent, canActivate: [MsalGuard] },
  { path: 'auditLog/:userId', component: OpmAuditLogGridComponent, canActivate: [MsalGuard] },
  { path: 'editProfile/:userId', component: OpmEditProfileComponent, canActivate: [MsalGuard] },
  { path: 'createProfile/:userId', component: OpmCreateuserProfileComponent, canActivate: [MsalGuard] },
  { path: 'containermappings', component: OpmContainermappingsComponent, canActivate: [MsalGuard] },
  { path: 'basemappings', component: OpmBasemappingsComponent, canActivate: [MsalGuard] },
  { path: 'onboardedapps', component: OpmOnboardedappsComponent, canActivate: [MsalGuard] },
  { path: 'dq-gate', component: OpmDqGateReviewComponent, canActivate: [MsalGuard] },
  { path: '**', redirectTo: '' }
];

// MSAL factory functions using the config service
export function MSALInstanceFactory(msalConfigService: MsalConfigService): IPublicClientApplication {
  const config = msalConfigService.getMsalConfig();
  return new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      redirectUri: config.redirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  });
}

export function MSALGuardConfigFactory(msalConfigService: MsalConfigService): MsalGuardConfiguration {
  const config = msalConfigService.getMsalConfig();
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [
        'openid',
        'profile',
        'user.read',
        config.scope
      ],
    },
  };
}

export function MSALInterceptorConfigFactory(msalConfigService: MsalConfigService): MsalInterceptorConfiguration {
  const config = msalConfigService.getMsalConfig();
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(config.redirectUri + '/api', [config.scope]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// MSAL v4 requires explicit initialization before any other API is used.
// APP_INITIALIZER ensures initialize() resolves before app bootstrap completes.
export function MSALInitializerFactory(msalService: MsalService): () => Promise<void> {
  return () => msalService.instance.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    OpmGrid,
    OpmSearchComponent,
    OpmAuditLogGridComponent,
    OpmEditProfileComponent,
    ModalPopupComponent,
    CurrentUserComponent,
    HelpMenuComponent,
    UserPhotoComponent,
    BooleanFormatterPipe,
    MilisecondsToSecondsPipe,
    MilisecondsToMinutesPipe,
    OpmPagingComponent,
    TimeoutComponent,
    OpmCreateuserProfileComponent,
    OpmContainermappingsComponent,
    OpmBasemappingsComponent,
    OpmOnboardedappsComponent,
    OpmDqGateReviewComponent
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' }),
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      closeButton: true,
      progressBar: true,
      enableHtml: false,
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info',
        success: 'toast-success',
        warning: 'toast-warning'
      }
    }),
    FormsModule,
    NgSelectModule,
    MsalModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlingInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService
    },
    // MSAL providers
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [MsalConfigService]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [MsalConfigService]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [MsalConfigService]
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: MSALInitializerFactory,
      deps: [MsalService],
      multi: true
    },
    //Repositories
    UserRepository,
    ContainerMappingRepository,
    //Services
    UserService,
    PopupService,
    TimeoutService,
    GridResultService,
    PageService,
    ApplicationInsightsService,
    ContainermappingService,
    DqGateRepository,
    DqGateService,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule { }
